import cx from 'classnames';
import {isUndefined} from 'lodash';
import {ReactNode, isValidElement, cloneElement, useState, useCallback, useMemo, useEffect, Children} from 'react';

import {Icon} from 'components';

type TitleProps = {
    children: ReactNode;
    iconPlacement?: 'left' | 'right'
    expanded?: boolean;
    styled?: boolean;
    className?: string;
    onClick?: () => void;
};

export const AccordionTitle: React.FC<TitleProps> = ({
    children, expanded, iconPlacement = 'left', className, styled = true, onClick
}: TitleProps) => (
    <div className={cx('tw-flex tw-items-center tw-cursor-pointer', className)} onClick={onClick}>
        <Icon
            primary={expanded}
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size='sm'
            className={cx({'tw-order-1': iconPlacement === 'right'})}
        />
        <div className='tw-w-full'>
            {styled && <div className='tw-ts-h5 tw-ml-2'>{children}</div>}
            {!styled && children}
        </div>
    </div>
);

type ShortDetailsProps = {
    children: ReactNode;
    className?: string;
    showExpanded?: boolean;
    expanded?: boolean;
};

export const AccordionShortDetails: React.FC<ShortDetailsProps> = ({expanded, showExpanded, className, children}: ShortDetailsProps) => {
    const showContent = useMemo(() => showExpanded || !expanded, [showExpanded, expanded]);
    return (showContent && <div className={cx(className, 'tw-mt-2')}>{children}</div>);
};

type DetailsProps = {
    children: ReactNode;
    className?: string;
    expanded?: boolean;
};

export const AccordionDetails: React.FC<DetailsProps> = ({expanded, children, className}: DetailsProps) =>
    expanded && <div className={cx(className, 'tw-mt-2', {'tw-hidden': !expanded})}>{children}</div>;

type Props = {
    children: ReactNode;
    defaultExpanded?: boolean;
    expanded?: boolean;
    padding?: boolean;
    shadow?: boolean;
    className?: string;
    onChange?: (expanded?: boolean) => void;
}

export const Accordion: React.FC<Props> = ({
    defaultExpanded, expanded, className, children,
    padding = true, shadow = true, onChange
}: Props) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(expanded ?? defaultExpanded ?? false);

    const toggleIsExpandedState = useCallback(() => {
        setIsExpanded(!isExpanded);
        onChange?.(!isExpanded);
    }, [isExpanded, onChange]);

    useEffect(() => {
        if (!isUndefined(expanded)) {
            setIsExpanded(expanded);
        }
    }, [expanded]);

    const childrenProps = useMemo(() => ({
        expanded: isExpanded,
        onClick: toggleIsExpandedState
    }), [isExpanded, toggleIsExpandedState]);

    return (
        <div
            className={cx(
                'tw-bg-light tw-transition-all tw-duration-500',
                {'tw-py-4 tw-px-5': padding, 'tw-shadow-md': shadow},
                className
            )}
        >
            {Children.map(children, (child) =>
                isValidElement(child) ? cloneElement(child, childrenProps) : null)}
        </div>
    );
};

export type AccordionsExpandedMap = Record<string, boolean>;